import type { LocationId } from "./locations";

export type MenuItem = { name: string; price: number; description: string; image: string };
export type MenuCategory = { name: string; image: string; items: MenuItem[] };

const placeholderImage = "https://cdn.magicpatterns.com/patterns/generated-images/3618f56a-be70-495d-a26d-ff6cbf0afe4e.jpg";

const IMG_APPETIZER = "https://cdn.magicpatterns.com/patterns/generated-images/8b6dd756-cb14-4d71-82bb-04dc4fd42653.jpg";
const IMG_HEARTY = "https://cdn.magicpatterns.com/patterns/generated-images/b80f26a0-b172-4c9a-bf25-e63c0c00bc6d.jpg";
const IMG_SIZZLE = "https://cdn.magicpatterns.com/patterns/generated-images/3618f56a-be70-495d-a26d-ff6cbf0afe4e.jpg";
const IMG_DRINK = "https://cdn.magicpatterns.com/patterns/generated-images/e6454b55-0eb7-4eb6-a1aa-696d234301b9.jpg";

const polishDescription = (description: string) =>
  description
    .replace(/served with/gi, "paired with")
    .replace(/served alongside/gi, "presented alongside")
    .replace(/served warm/gi, "presented warm")
    .replace(/served ice-cold/gi, "poured ice-cold")
    .replace(/perfectly/gi, "carefully")
    .replace(/perfect/gi, "ideal");

const createItem = (name: string, price: number, description: string): MenuItem => ({
  name,
  price,
  description: polishDescription(description),
  image: placeholderImage,
});

// Shared menu: the official Agave Family / Blue Agave menu is unified across both
// Driggs and Victor locations. Names and prices below match the owner-provided menu exactly;
// only the descriptive copy has been rewritten in a premium, consistent editorial voice.
const sharedMenu: MenuCategory[] = [
  {
    name: "Appetizers",
    image: IMG_APPETIZER,
    items: [
      createItem("Guacamole", 5, "Hand-mashed ripe avocado blended with fresh lime, onion and cilantro, served with warm tortilla chips."),
      createItem("Cheese Dip", 5, "Warm, melted cheese blended smooth and served with crisp, freshly fried tortilla chips."),
      createItem("Choriqueso", 8, "Melted cheese folded with savory Mexican chorizo, served warm with fresh tortilla chips."),
      createItem("Quesadillas", 20, "Grilled flour tortillas filled with melted cheese, folded golden and crisp on the comal."),
      createItem("Nachos Deluxe", 20, "Crisp tortilla chips layered with melted cheese and traditional toppings, baked until golden."),
      createItem("Cheese Quesadillas", 15, "Melted cheese folded inside a warm flour tortilla, grilled until golden and crisp."),
      createItem("Tortilla Soup", 9, "Traditional tomato broth simmered with authentic spices, topped with crisp tortilla strips."),
    ],
  },
  {
    name: "Tacos",
    image: IMG_HEARTY,
    items: [
      createItem("Three Street Tacos", 20, "Three authentic street-style tacos on warm corn tortillas with fresh onion and cilantro."),
      createItem("Single Taco", 5, "One authentic street taco on a warm corn tortilla with fresh onion and cilantro."),
    ],
  },
  {
    name: "Burritos",
    image: IMG_SIZZLE,
    items: [
      createItem("Carne Asada Burrito", 20, "Flame-grilled steak wrapped in a warm flour tortilla with Mexican rice and beans."),
      createItem("Chile Verde Burrito", 17, "Tender pork simmered in tangy tomatillo sauce, wrapped in a warm flour tortilla."),
      createItem("Al Pastor Burrito", 20, "Marinated pork wrapped in a warm flour tortilla with traditional Mexican rice and beans."),
      createItem("Chipotle Burrito", 17, "Your choice of protein bathed in smoky chipotle sauce, wrapped in a warm tortilla."),
      createItem("Carnitas Burrito", 20, "Slow-braised pork wrapped in a warm flour tortilla with traditional rice and beans."),
      createItem("Seafood Burrito", 20, "Sautéed shrimp and fish wrapped in a warm flour tortilla with rice and beans."),
      createItem("Fajita Steak Burrito", 20, "Sizzling grilled steak with sautéed peppers and onions, wrapped in a warm tortilla."),
      createItem("Super Burrito", 20, "A hearty burrito filled with your choice of meat, Mexican rice, beans and melted cheese."),
      createItem("Fajita Chicken Burrito", 17, "Sizzling grilled chicken with sautéed peppers and onions, wrapped in a warm tortilla."),
      createItem("Ground Beef Burrito", 17, "Seasoned ground beef wrapped in a warm flour tortilla with Mexican rice and beans."),
      createItem("Picadillo Burrito", 17, "Traditional picadillo-style ground beef wrapped in a warm flour tortilla with rice and beans."),
      createItem("Chicken Burrito", 17, "Grilled chicken wrapped in a warm flour tortilla with Mexican rice and beans."),
      createItem("Veggie Burrito", 17, "Sautéed seasonal vegetables wrapped in a warm flour tortilla with rice and beans."),
      createItem("Bean & Cheese Burrito", 17, "Slow-simmered beans and melted cheese wrapped inside a warm flour tortilla."),
      createItem("Cheese Burrito", 17, "Melted cheese wrapped inside a warm flour tortilla, simple and satisfying."),
    ],
  },
  {
    name: "Enchiladas",
    image: IMG_APPETIZER,
    items: [
      createItem("Suiza Enchilada", 17, "Corn tortillas filled with chicken, topped with green sauce and sour cream."),
      createItem("Mole Enchilada", 17, "Chicken sautéed in our traditional, authentic, slow-crafted mole sauce, prepared fresh to order."),
      createItem("Enchilada a la Crema", 17, "Corn tortillas filled with your choice of protein, finished in a smooth cream sauce."),
      createItem("Seafood Enchilada", 17, "Shrimp and real crab meat cooked in butter, folded into corn tortillas with fresh salsa."),
      createItem("Chipotle Enchilada", 17, "Corn tortillas filled with chicken in our traditional, smoky chipotle sauce."),
      createItem("Chile Verde Enchilada", 17, "Corn tortillas filled with your choice of protein, topped with tangy tomatillo sauce."),
      createItem("Ground Beef Enchilada", 17, "Corn tortillas filled with seasoned ground beef, finished with traditional enchilada sauce."),
      createItem("Picadillo Enchilada", 17, "Corn tortillas filled with traditional picadillo-style ground beef and authentic enchilada sauce."),
      createItem("Chicken Enchilada", 17, "Corn tortillas filled with tender chicken, finished with traditional enchilada sauce."),
      createItem("Veggie Enchilada", 17, "Corn tortillas filled with sautéed seasonal vegetables and traditional enchilada sauce."),
      createItem("Cheese Enchilada", 17, "Corn tortillas filled with melted cheese, finished with traditional enchilada sauce."),
    ],
  },
  {
    name: "Beef Dishes",
    image: IMG_HEARTY,
    items: [
      createItem("Steak Ranchero", 25, "Grilled steak topped with our traditional, slow-crafted house-made ranchero sauce."),
      createItem("Carne Asada", 25, "Flame-grilled steak served with handmade tortillas, Mexican rice and traditional beans."),
      createItem("Tampiqueña", 25, "Grilled steak served alongside a cheese enchilada, Mexican rice, beans and guacamole."),
      createItem("Carnitas de Res", 22, "Slow-braised beef prepared with authentic spices in traditional Mexican style."),
      createItem("Milanesa de Res", 25, "Thin breaded beef cutlet, fried golden and served with rice and beans."),
      createItem("Chile Verde", 17, "Tender pork simmered slowly in our tangy, house-made tomatillo sauce."),
      createItem("Chuletas de Puerco", 22, "Grilled pork chops seasoned with authentic spices, served with rice and beans."),
    ],
  },
  {
    name: "Fajitas",
    image: IMG_SIZZLE,
    items: [
      createItem("Steak Fajitas", 22, "Sizzling grilled steak with sautéed peppers and onions, served with warm handmade tortillas."),
      createItem("Chicken Fajitas", 22, "Sizzling grilled chicken with sautéed peppers and onions, served with warm handmade tortillas."),
      createItem("Prawns Fajitas", 22, "Sizzling grilled prawns with sautéed peppers and onions, served with warm handmade tortillas."),
      createItem("Texas Fajitas", 22, "Sizzling steak, chicken and prawns with sautéed peppers and onions on warm tortillas."),
      createItem("Veggie Fajitas", 18, "Sizzling seasonal vegetables sautéed with peppers and onions, served with warm tortillas."),
    ],
  },
  {
    name: "Chicken Dishes",
    image: IMG_APPETIZER,
    items: [
      createItem("Pollo Chipotle", 22, "Grilled chicken finished in our smoky, traditional house-made chipotle sauce."),
      createItem("Pollo a la Crema", 22, "Tender chicken simmered in a smooth, traditional Mexican cream sauce."),
      createItem("Pollo en Mole", 22, "Tender chicken finished with our slow-crafted, traditional Mexican mole sauce."),
      createItem("Pollo con Queso", 23, "Grilled chicken topped generously with warm, melted traditional Mexican cheese."),
      createItem("Carnitas de Pollo", 22, "Slow-braised chicken prepared with authentic spices in traditional Mexican style."),
      createItem("Pollo Asado", 22, "Flame-grilled chicken seasoned with authentic spices, served with rice and beans."),
      createItem("Milanesa de Pollo", 23, "Thin breaded chicken cutlet, fried golden and served with rice and beans."),
    ],
  },
  {
    name: "Seafood",
    image: IMG_HEARTY,
    items: [
      createItem("Blue Agave Special", 22, "Our signature seafood dish, prepared with fresh shrimp and traditional coastal flavors."),
      createItem("Camarones Apretalados", 23, "Shrimp wrapped in crisp bacon, grilled and served with rice and beans."),
      createItem("Camarones al Mojo de Ajo", 22, "Fresh shrimp sautéed in a rich, aromatic garlic butter sauce, full of flavor."),
      createItem("Camarones a la Diabla", 22, "Fresh shrimp simmered in a spicy, vibrant red chile sauce."),
      createItem("Camarones en Salsa Verde", 23, "Fresh shrimp simmered in a bright, tangy tomatillo salsa verde."),
      createItem("Camarones al Chipotle", 22, "Fresh shrimp bathed in a smoky, traditional house-made chipotle sauce."),
      createItem("Pescado Empanizado", 22, "Breaded fish fillet fried golden, served with rice, beans and salad."),
      createItem("Mojarra", 22, "Whole fried tilapia prepared in traditional Mexican coastal style, crisp and flavorful."),
      createItem("7 Mares", 25, "A rich seafood broth simmered with a variety of fresh ocean flavors."),
      createItem("Aguachile", 23, "Fresh shrimp cured in vibrant lime juice with cucumber, onion and chile."),
      createItem("Orden de Ceviche", 22, "Fresh seafood marinated in citrus with onion, cilantro and tomato."),
      createItem("Orden de Ceviche con Pulpo", 27, "Fresh ceviche blended with tender octopus, marinated in bright citrus."),
      createItem("Cóctel Campechano", 25, "A chilled mix of fresh shrimp and seafood in traditional cocktail sauce."),
      createItem("Cóctel de Camarón", 20, "Chilled fresh shrimp served in our traditional, house-made cocktail sauce."),
      createItem("Tostada de Ceviche", 6, "A crisp tostada topped generously with fresh, citrus-marinated seafood ceviche."),
      createItem("Tostada de Ceviche con Pulpo", 8, "A crisp tostada topped with fresh ceviche and tender octopus."),
    ],
  },
  {
    name: "Salad & Tostada",
    image: IMG_SIZZLE,
    items: [
      createItem("Taco Salad Deluxe", 20, "Choice of chicken, beef, picadillo or beans served with guacamole and sour cream."),
      createItem("Tostada Deluxe", 20, "Choice of chicken, beef, picadillo or beans served with guacamole and sour cream."),
      createItem("Blue Agave Salad", 17, "Fresh mixed greens topped with tomatoes, cheese and your choice of protein."),
      createItem("Pollo Asado Salad", 17, "Tomatoes, lettuce and cheese with char-broiled, marinated chicken breast."),
      createItem("Seafood Salad", 17, "Real crab and baby shrimp with fresh lettuce and tomatoes, topped with cheese."),
      createItem("Bean Avocado Tostada", 17, "A crisp tostada topped with refried beans and fresh avocado slices."),
    ],
  },
  {
    name: "Dishes for Two",
    image: IMG_APPETIZER,
    items: [
      createItem("Combination for Two", 45, "An elegant assortment of our finest traditional dishes, crafted for sharing together."),
      createItem("Fajitas for Two", 40, "A sizzling platter of grilled steak, chicken and prawns, crafted for sharing."),
      createItem("Fajitas for Three", 47, "A generous sizzling platter of grilled steak, chicken and prawns for three."),
    ],
  },
  {
    name: "Kids",
    image: IMG_HEARTY,
    items: [
      createItem("Kids Enchiladas", 9, "A mild enchilada crafted with tender fillings, perfect for younger guests."),
      createItem("Kids Burritos", 9, "A perfectly sized, mild burrito made fresh for our younger guests."),
      createItem("Kids Quesadillas", 9, "Melted cheese folded inside a warm flour tortilla, made for kids."),
      createItem("Kids Taco", 9, "A simple, mild taco crafted with fresh ingredients for younger guests."),
      createItem("Kids Chicken Fingers", 9, "Crispy breaded chicken tenders served with a side of golden fries."),
    ],
  },
  {
    name: "Combinations",
    image: IMG_SIZZLE,
    items: [
      createItem("Combo Poquito", 13, "Your choice of one item: enchilada, taco, tostada, tamal, chimichanga, burrito, chalupa or chile relleno."),
      createItem("Combo Medio", 14, "Your choice of two items: enchilada, taco, tostada, tamal, chimichanga, burrito, chalupa or chile relleno."),
      createItem("Combo Medio Premium", 15, "Your choice of two premium items: enchilada, taco, tostada, tamal, chimichanga, burrito or chile relleno."),
      createItem("Combo Grande", 16, "Your choice of three items: enchilada, taco, tostada, tamal, chimichanga, burrito, chalupa or chile relleno."),
      createItem("Combo Grande Premium", 17, "Your choice of three premium items: enchilada, taco, tostada, tamal, chimichanga, burrito or chile relleno."),
      createItem("Flautas", 17, "Crisp rolled tortillas filled with seasoned meat, fried golden and garnished fresh."),
    ],
  },
  {
    name: "Desserts",
    image: IMG_APPETIZER,
    items: [
      createItem("Fried Ice Cream", 8, "Crisp golden coating around cool vanilla ice cream, dusted with cinnamon sugar."),
      createItem("Flan", 7, "Our house-made traditional Mexican custard, finished with rich caramel sauce."),
      createItem("Sopapillas", 7, "Warm fried pastry dusted with cinnamon sugar and drizzled with honey."),
      createItem("Churros", 7, "Golden fried pastry coated in cinnamon sugar, served with warm chocolate dipping sauce."),
      createItem("Fried Bananas", 7, "Sweet plantains fried golden, a warm and comforting traditional dessert."),
    ],
  },
  {
    name: "Specials",
    image: IMG_HEARTY,
    items: [
      createItem("Lunch Especial", 13, "A curated selection of traditional Mexican favorites, crafted for a satisfying lunch."),
      createItem("Dinner Especial", 20, "An elevated combination of our traditional, handcrafted Mexican dinner favorites."),
      createItem("Menudo", 22, "A traditional, slow-simmered Mexican soup crafted with authentic spices and hominy."),
    ],
  },
  {
    name: "Lunch Combinations",
    image: IMG_SIZZLE,
    items: [
      createItem("1 - Cheese Enchilada & Taco", 14, "A classic pairing of a cheese enchilada and an authentic street taco."),
      createItem("2 - Chile Relleno, 2 Tortillas", 14, "A traditional stuffed pepper served with two warm handmade tortillas."),
      createItem("3 - Cheese Enchilada & Bean Tostada", 14, "A cheese enchilada paired with a crisp, traditional bean tostada."),
      createItem("4 - Tamal & Taco", 14, "Our traditional, slow-crafted tamal served alongside an authentic street taco."),
      createItem("5 - Cheese Enchilada & Tamal", 14, "A cheese enchilada paired with our traditional, slow-crafted tamal."),
      createItem("6 - Chimichanga", 17, "A crisp, golden fried burrito filled with your choice of protein."),
      createItem("7 - Flautas", 14, "Crisp rolled tortillas filled with seasoned meat and fried golden."),
      createItem("8 - Alambre", 14, "Grilled meat sautéed with peppers, onions, bacon and melted cheese."),
      createItem("9 - Carnitas de Res", 14, "Slow-braised beef prepared with authentic spices in traditional Mexican style."),
      createItem("10 - Chile Verde", 14, "Tender pork simmered slowly in our tangy, house-made tomatillo sauce."),
      createItem("11 - Chuletas de Puerco", 14, "Grilled pork chops seasoned with authentic spices and traditional flavor."),
      createItem("12 - Pollo a la Crema", 14, "Tender chicken simmered in a smooth, traditional Mexican cream sauce."),
      createItem("13 - Pollo en Mole", 14, "Tender chicken finished with our slow-crafted, traditional Mexican mole sauce."),
      createItem("14 - Pollo Chipotle", 14, "Grilled chicken finished in our smoky, traditional house-made chipotle sauce."),
      createItem("15 - Pollo Asado", 14, "Flame-grilled chicken seasoned with authentic spices and traditional flavor."),
      createItem("16 - Carnitas de Pollo", 14, "Slow-braised chicken prepared with authentic spices in traditional Mexican style."),
      createItem("17 - Apretalados", 14, "Shrimp wrapped in crisp bacon, grilled until golden and smoky."),
      createItem("18 - Camarones a la Chipotle", 14, "Fresh shrimp bathed in a smoky, traditional house-made chipotle sauce."),
      createItem("19 - Camarones al Mojo de Ajo", 14, "Fresh shrimp sautéed in a rich, aromatic garlic butter sauce."),
      createItem("20 - Camarones a la Diabla", 14, "Fresh shrimp simmered in a spicy, vibrant red chile sauce."),
      createItem("21 - Blue Agave Especial", 14, "Our signature lunch seafood dish, prepared with fresh shrimp and traditional flavors."),
    ],
  },
  {
    name: "Lunch Enchiladas",
    image: IMG_APPETIZER,
    items: [
      createItem("Suiza Enchilada", 14, "A chicken enchilada topped with traditional green sauce and sour cream."),
      createItem("Mole Enchilada", 14, "Chicken sautéed in our traditional, authentic, slow-crafted mole sauce, prepared fresh to order."),
      createItem("Seafood Enchilada", 14, "Shrimp and real crab meat cooked in butter with fresh chopped salsa."),
      createItem("Chipotle Enchilada", 14, "A chicken enchilada finished in our traditional, smoky chipotle sauce."),
      createItem("Enchilada a la Crema", 14, "Corn tortillas filled with your choice of protein in a smooth cream sauce."),
      createItem("Choice of Enchilada", 14, "Your choice of ground beef, picadillo or chicken, finished with traditional sauce."),
      createItem("Cheese Enchilada", 14, "Corn tortillas filled with melted cheese, finished with traditional enchilada sauce."),
      createItem("Veggie Enchilada", 14, "Corn tortillas filled with sautéed seasonal vegetables and traditional enchilada sauce."),
      createItem("Chile Verde Enchilada", 14, "Corn tortillas topped with our tangy, house-made green tomatillo sauce."),
      createItem("Bean & Cheese Enchilada", 14, "Corn tortillas filled with slow-simmered beans and melted traditional cheese."),
      createItem("Chile Colorado Enchilada", 14, "Corn tortillas bathed in our rich, traditional red chile sauce."),
    ],
  },
  {
    name: "Lunch Burritos",
    image: IMG_HEARTY,
    items: [
      createItem("Seafood Burrito", 14, "Sautéed shrimp and fish wrapped in a warm flour tortilla."),
      createItem("Burrito Chipotle", 14, "Your choice of protein bathed in smoky chipotle sauce, wrapped warm."),
      createItem("Chicken Fajitas Burrito", 14, "Sizzling grilled chicken with sautéed peppers and onions in a warm tortilla."),
      createItem("Steak Fajitas Burrito", 14, "Sizzling grilled steak with sautéed peppers and onions in a warm tortilla."),
      createItem("Chile Verde Burrito", 14, "Tender pork simmered in tangy tomatillo sauce, wrapped in a warm tortilla."),
      createItem("Chorizo Burrito", 14, "Savory Mexican chorizo wrapped in a warm flour tortilla with rice."),
      createItem("Choice of Burrito", 14, "Your choice of ground beef, picadillo or chicken, wrapped in a warm tortilla."),
      createItem("Bean Burrito", 14, "Slow-simmered traditional beans wrapped inside a warm flour tortilla."),
      createItem("Bean & Cheese Burrito", 14, "Slow-simmered beans and melted cheese wrapped inside a warm tortilla."),
      createItem("Veggie Burrito", 14, "Sautéed seasonal vegetables wrapped in a warm flour tortilla with rice."),
      createItem("Chile Colorado Burrito", 14, "Tender meat in rich red chile sauce, wrapped in a warm tortilla."),
    ],
  },
  {
    name: "Lunch Salads",
    image: IMG_SIZZLE,
    items: [
      createItem("Taco Salad Deluxe", 14, "Choice of chicken, beef, picadillo or beans with guacamole and sour cream."),
      createItem("Pollo Asado Salad", 14, "Tomatoes, lettuce and cheese with char-broiled, marinated chicken breast."),
      createItem("Seafood Salad", 14, "Real crab and baby shrimp with fresh lettuce, tomatoes and cheese, your choice of dressing."),
    ],
  },
  {
    name: "Lunch Tostadas",
    image: IMG_APPETIZER,
    items: [
      createItem("Tostada Deluxe", 14, "Choice of chicken, beef, picadillo or beans with guacamole and sour cream."),
      createItem("Bean Avocado Tostada", 14, "A crisp tostada topped with beans and fresh avocado slices."),
    ],
  },
  {
    name: "Egg Dishes",
    image: IMG_HEARTY,
    items: [
      createItem("Chorizo con Huevos", 14, "Savory Mexican chorizo scrambled with eggs, onions and fresh tomatoes."),
      createItem("Huevos a la Mexicana", 14, "Scrambled eggs cooked with fresh tomatoes, onions and jalapeños."),
      createItem("Machaca", 14, "Scrambled eggs with shredded skirt steak, peppers, tomatoes and onions."),
      createItem("Huevos Rancheros", 14, "Three fried eggs topped with melted cheese and traditional green salsa."),
      createItem("Huevos con Jamón", 14, "Scrambled eggs with savory diced ham, cooked fresh to order."),
    ],
  },
  {
    name: "Lunch Specials",
    image: IMG_SIZZLE,
    items: [
      createItem("Special 1", 12, "A lighter, curated selection of traditional Mexican favorites for lunch."),
      createItem("Special 2", 13, "An elevated, generous selection of traditional Mexican favorites for lunch."),
    ],
  },
  {
    name: "Beverages",
    image: IMG_DRINK,
    items: [
      createItem("Soft Drinks", 3.5, "A classic, refreshing fountain beverage, served ice-cold to accompany your meal."),
      createItem("Coca-Cola de Botella", 4, "Classic Coca-Cola served chilled in a traditional glass bottle."),
      createItem("Coca-Cola Grande", 5, "A generous, refreshing serving of ice-cold classic Coca-Cola."),
      createItem("Pepsi", 3.5, "A classic, refreshing fountain cola, served ice-cold and crisp."),
      createItem("Diet Pepsi", 3.5, "A crisp, sugar-free fountain cola, served ice-cold and refreshing."),
      createItem("7Up", 3.5, "A crisp, refreshing lemon-lime soda, served ice-cold."),
      createItem("Mountain Dew", 3.5, "A bold, citrus-flavored fountain soda, served ice-cold."),
      createItem("Dr Pepper", 3.5, "A classic, richly spiced fountain soda, served ice-cold."),
      createItem("Root Beer", 3.5, "A smooth, classic root beer, served ice-cold and refreshing."),
      createItem("Lemonade", 3.5, "Freshly made, bright and refreshing traditional lemonade, served ice-cold."),
      createItem("Sunkist", 3.5, "A bright, citrus-orange fountain soda, served ice-cold."),
      createItem("Shirley Temple", 4, "A classic, sweet non-alcoholic favorite finished with a cherry garnish."),
      createItem("Roy Rogers", 4, "A refreshing, sweet non-alcoholic classic made with cola and cherry."),
      createItem("Arnold Palmer", 4, "A balanced, refreshing blend of iced tea and sweet lemonade."),
    ],
  },
];

export const driggsMenu: MenuCategory[] = sharedMenu;
export const victorMenu: MenuCategory[] = sharedMenu;

export const sides: MenuItem[] = [
  createItem("Rice", 2.5, "Traditional Mexican rice, steamed until light, fluffy and flavorful."),
  createItem("Beans", 2.5, "Slow-simmered traditional beans, rich, hearty and full of flavor."),
  createItem("Rice & Beans", 5, "The classic pairing of traditional Mexican rice and slow-simmered beans."),
  createItem("Chimichanga", 8, "A crisp, golden fried burrito filled with your choice of protein."),
  createItem("Chile Rellenos", 7, "A traditional stuffed poblano pepper, battered and fried until golden brown."),
  createItem("Burrito", 4, "A simple burrito filled with your choice and wrapped in a warm tortilla."),
  createItem("Enchilada", 4, "A single corn tortilla enchilada, filled and finished with traditional sauce."),
  createItem("Taco", 2.5, "A single authentic street taco served on a warm corn tortilla."),
  createItem("Tamal", 5, "Our traditional, slow-crafted tamal wrapped in a corn husk."),
  createItem("Fried Jalapeño", 1.5, "One whole jalapeño pepper, lightly battered and fried until golden brown."),
  createItem("Guacamole", 5, "Hand-mashed ripe avocado with fresh lime, onion and cilantro."),
  createItem("Sour Cream", 2, "A generous side of cool, rich traditional Mexican sour cream."),
  createItem("Chips & Salsa", 5, "Crisp fried tortilla chips served with fresh, house-made salsa."),
  createItem("Extra Salsa", 2, "An additional portion of our fresh, vibrant house-made salsa."),
  createItem("Extra Chips", 2, "An additional portion of our crisp, freshly fried tortilla chips."),
  createItem("Extra Cabbage", 2, "An additional portion of fresh, crisp shredded cabbage for topping."),
  createItem("Crema & Guacamole", 4, "A pairing of cool sour cream and fresh, hand-mashed guacamole."),
  createItem("Dinner Salad", 5, "A fresh mixed green salad with tomato, served alongside your meal."),
  createItem("Tortillas", 2, "Warm corn or flour tortillas, essential to any authentic Mexican meal."),
  createItem("Cheese", 1, "A generous portion of warm, melted traditional Mexican cheese for finishing any dish."),
  createItem("Mole Sauce", 2, "A side of our traditional, slow-crafted, authentic Mexican mole sauce."),
  createItem("Chipotle Sauce", 2, "A side of our smoky, traditional house-made chipotle sauce, prepared fresh daily."),
  createItem("Limón", 2, "Fresh lime wedges, ideal for brightening any authentic Mexican dish."),
  createItem("Pico de Gallo", 4, "Fresh diced tomato, onion, cilantro and jalapeño, chopped fresh daily."),
  createItem("Fries", 4, "A generous portion of crisp, golden French fries, fried hot."),
  createItem("Huevo", 3, "One fresh egg, prepared to crown and enrich your selected dish."),
  createItem("Salsa Small", 5, "A small container of our fresh, vibrant house-made salsa."),
  createItem("Salsa Medium", 10, "A medium container of our fresh, vibrant house-made salsa."),
  createItem("Cabbage Medium", 5, "A medium portion of fresh, crisp shredded cabbage for topping."),
  createItem("Cabbage Large", 10, "A large portion of fresh, crisp shredded cabbage for topping."),
  createItem("Fried Jalapeños", 1, "Whole jalapeño peppers, lightly battered and fried until golden brown."),
];

export const menus: Record<LocationId, MenuCategory[]> = { driggs: driggsMenu, victor: victorMenu };
export const menuItemCount = (menu: MenuCategory[]) => menu.reduce((sum, category) => sum + category.items.length, 0);
